// Datasheet -> NetSuite inventory-item field extraction.
// Labels match backend netsuite.py FIELD_DEFS exactly.
import { BADGE_GROUPS } from './badges';

export const NS_GROUPS = [
  {
    title: 'Item',
    labels: ['ITEM NAME/NUMBER INTERNAL', 'DISPLAY NAME/CODE-SALES', 'VENDOR NAME/CODE',
      'SALES DESCRIPTION', 'PURCHASE DESCRIPTION', 'INVENTORY CATEGORY', 'INVENTORY STATUS',
      'TYPE', 'REPRESENTATIVE BRAND', 'DATASHEET LINK', 'ASSOCIATED PRODUCT/OLD EVO CODE', 'PRODUCT IMAGE'],
  },
  {
    title: 'Key Features',
    labels: ['FAMILY', 'SUB CATEGORY', 'SERIES', 'FITTING COLOUR', 'MAIN MATERIAL',
      'ACCESSORIES/OPTIONS', 'OPTICAL SYSTEM', 'REFLECTOR/BAFFLE COLOUR', 'TILT', 'ROTATION',
      'CERTIFICATIONS'],
  },
  {
    title: 'Electrical Data',
    labels: ['SYSTEM WATTAGE (W)', 'SOURCE WATTAGE (W)', 'SUPPLY VOLTAGE (V)', 'POWER FACTOR (PF)',
      'OPERATING TEMPERATURE (TA)', 'CLASS OF PROTECTION', 'INGRESS PROTECTION RATING (IP)',
      'IMPACT RESISTANCE RATING (IK)', 'POWER CONNECTION', 'DIMMING OPTIONS', 'DRIVER BRAND/CODE',
      'OUTPUT VOLTAGE (V)'],
  },
  {
    title: 'Photometric Performance',
    labels: ['LIGHT SOURCE BRAND', 'LIGHT SOURCE TYPE', 'SYSTEM LUMENS (LM)', 'SOURCE LUMENS (LM)',
      'EFFICACY (LM/W)', 'CRI', 'COLOUR TEMP (CCT)', 'MACADAM', 'LED LIFETIME (HRS)', 'UGR',
      'LOR %', 'BEAM ANGLE', 'ENERGY CLASS', 'ELC ITEM CODE', 'DRIVER MA', 'VENDOR WARRANTY'],
  },
];

export const NS_MULTILINE = ['SALES DESCRIPTION', 'PURCHASE DESCRIPTION'];
export const AU_SUBSIDIARY_ID = '13'; // "LPA Group : Australia / New Zealand : Australia"

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

// Dropdown options for the push review page.
// Source: "Item Attributes - Single & Multi Selects.xlsx" (kept in sync with NetSuite lists).
export const NS_SELECT_OPTIONS = {
  'SUB CATEGORY': [
    '48V - Isse VI (IS6)', '48V - Powergear (IS2)', 'Accessory & Components (AC)',
    'Adjustable Track Spot (ST)', 'Batten (BT)', 'Bollard (BL)', 'Cabinet & Showcase (CL)',
    'Electronics & Lighting Control (EC)', 'Exit & Emergency (EL)', 'Fixed Track Spot (DT)',
    'Flexible Led Strip & Aluminium Profile (FLS)', 'Floodlight (FL)', 'Free Standing Luminaire (FS)',
    'Garden (GD)', 'Ground Mount (GM)', 'Highbay, Lowbay & Canopy (HB)', 'In-Ground (IG)',
    'Lamps, Bulbs, Globes, Tubes (CO)', 'Linear Recessed (LN)', 'Linear Surface Mounted (LNSM)',
    'Linear Suspended (LNSP)', 'Linear Wall Mounted (LNWM)', 'Modurail 13 Wire (MDR13)',
    'ModuRail 8 Wire (MDR8)', 'ModuRail Compatible (MDR)', 'Non Stock 48V System (ISX)',
    'Non Stock Trunking System (TRNK)', 'Non-Lighting Item (NL)', 'Panel (PA)',
    'Pendant Adjustable (SP)', 'Pendant Fixed (DP)', 'Pendant Track Spot (PT)',
    'Power Supply Unit (PS)', 'Recessed Adjustable (SR)', 'Recessed Fixed (DR)', 'Solar (SOL)',
    'Spot Light (SPL)', 'Street Light (SL)', 'Surface Adjustable (SS)', 'Surface Fixed (DS)',
    'Surface Mounted (SM)', 'Track & Components (TKC)', 'Troffer (TR)', 'Underwater Light (UW)',
    'USA Strip Light (USL)', 'Venix Lighting System (VEN)', 'Wall Light (WA)', 'WideSky (WS)',
  ],
  'INVENTORY CATEGORY': ['Customer Specific', 'Core Range'],
  'FITTING COLOUR': [
    'AL', 'Black', 'Black Chrome', 'Blue', 'Bronze', 'Brushed Ravenna Bronze', 'Brushed Steel',
    'Champagne Gold', 'Chrome', 'Clear', 'Gloss White', 'Gold', 'Green', 'Grey', 'Other',
    'Pale Umber (Champagne Gold)', 'Red', 'Rubra (Red)', 'Satin Ravenna Bronze', 'Silver',
    'Silver Denarius', 'Specular', 'Stainless Steel', 'White', 'Yellow',
  ],
  'OPTICAL SYSTEM': [
    'Lens (L)', 'Louvre (LO)', 'Microprismatic (MP)', 'MPRM+UGR Film (MPH)', 'Opal PMMA (OP)',
    'Polycarbonate Diffuser (PC)', 'Reflector (R)',
  ],
  'REFLECTOR/BAFFLE COLOUR': ['Black', 'Chrome', 'Chrome Black', 'Golden', 'Other', 'Silver', 'White'],
  'CLASS OF PROTECTION': ['CLASS I', 'CLASS II', 'CLASS III'],
  'INGRESS PROTECTION RATING (IP)': [
    'IP20', 'IP30', 'IP40', 'IP42', 'IP44', 'IP54', 'IP64', 'IP65', 'IP66', 'IP67', 'IP69K',
    'IPX2', 'IPX4', 'IPX6', 'Other',
  ],
  'IMPACT RESISTANCE RATING (IK)': [
    'IK01', 'IK02', 'IK03', 'IK04', 'IK05', 'IK06', 'IK07', 'IK08', 'IK09', 'IK10', 'NA',
  ],
  'POWER CONNECTION': ['Flex and Plug', 'Direct', 'Terminal Block', 'IEC Socket', 'WAGO Plug', 'Non-dimmable'],
  'LIGHT SOURCE BRAND': [
    'Bridgelux', 'Citizen', 'Cree CMA', 'Cree CMB', 'CREE CMB Pro 9', 'Cree CXA', 'Cree CXB',
    'Cree XPG2', 'Edison Opto', 'Epistar/Certaflux (Phillips)', 'Guineg', 'Hongli', 'Jufei',
    'LED Teen', 'LG Innotek', 'Lumens AC', 'Lumileds (Phillips)', 'Luminious', 'Nichia', 'Osram',
    'Phillips', 'Samsung', 'Sanan Optoelectronics', 'Soraa', 'Tipicled', 'Toyoda Gosel', 'Vivid',
  ],
  'LIGHT SOURCE TYPE': ['COB', 'LED Module', 'Light Bulb', 'Other', 'RGB', 'RGBW', 'SMD'],
  'CRI': ['>60', '>70', '>75', '>80', '>90', '>95', '>97', '<59', 'N/A'],
  'MACADAM': ['SDCM 1', 'SDCM 2', 'SDCM 3', 'SDCM 4', 'SDCM 5', 'SDCM 6'],
  'ENERGY CLASS': ['A', 'B', 'C', 'D', 'E', 'F', 'G'],
};

// Multi-pick fields. With `multiValue` set, NetSuite stores ONE value and >1
// picks collapse to that value; without it the field is a true NetSuite
// multi-select and the comma-joined list is pushed as-is.
export const NS_MULTI_SELECT = {
  'COLOUR TEMP (CCT)': {
    options: ['2300K', '2600K', '2700K', '3000K', '3500K', '3700K', '4000K', '4500K', '5000K',
      '5500K', '5700K', '6000K', '6500K', '7000K', 'RGB', 'RGBW', 'Switchable', 'Tuneable', 'NONE'],
    multiValue: 'Multi',
  },
  'ACCESSORIES/OPTIONS': {
    options: ['Anti Ligature (AG)', 'Asymmetric (AS)', 'Baffle (BF)', 'Barn Door (BD)',
      'Clear Diffuser (CD)', 'Clear glass (CG)', 'Clear IP Rated Lens (IC)', 'Cross Louver (CL)',
      'Custom Filter (CF)', 'Custom Lens (LE)', 'Elliptical Lens (EL)', 'Emergency Relay (ER)',
      'Frosted Diffuser (FD)', 'Frosted Glass (FG)', 'Frosted Lens (FL)', 'Glass Cover (GL)',
      'Honeycomb Louver (HC)', 'Integral Driver (ID)', 'IP54 / Damp (IP)', 'Lens and Snoot (LS)',
      'Opal Spreader Lens (OL)', 'Prismatic Diffuser (PR)', 'Reeded Diffuser (RE)',
      'Solite (Soft Focus Lens) (SO)', 'Solite IP Rated Lens (IS)', 'Spread Lens (SL)',
      'Symmetric (SY)', 'Wall Washer (WW)', 'Weiland Connector (WE)'],
  },
  'CERTIFICATIONS': {
    options: ['CB', 'CCC', 'CE', 'EN61000', 'ENEC', 'ENTA', 'ETL', 'FCC', 'iPART', 'NMI Approved',
      'RCM', 'ROHS', 'SAA', 'SMARK', 'TM-21', 'TM-79', 'UKCA', 'UL', 'VEET', 'Water Marked'],
  },
  'DIMMING OPTIONS': {
    options: ['Non-dimmable', 'Phase-Cut', 'DALI', 'DALI-2', '0-10V', '1-10V', 'DMX', 'Lutron',
      'No Driver', 'Triac', 'Casambi'],
  },
};

export function nsPushValue(label, value) {
  const cfg = NS_MULTI_SELECT[label];
  if (cfg && cfg.multiValue && String(value || '').includes(',')) return cfg.multiValue;
  return value;
}

export function nsImageUrl(productId, sheet) {
  if (!productId || !(sheet && sheet.productImage)) return '';
  return BACKEND + '/api/ns-image/' + productId;
}

const digits = (v) => String(v == null ? '' : v).replace(/[^0-9.]/g, '');

function seriesFromCode(code) {
  const c = String(code || '').replace(/^LPA-/i, '');
  return c.split('-').slice(0, 3).join('-');
}

export function nsValuesFromSheet(sheet, productId) {
  const s = sheet || {};
  const pd = s.productDetails || {};
  const cs = s.commonSpecs || {};
  const v0 = (s.variants || [])[0] || {};
  const on = Object.keys(s.badges || {}).filter(k => s.badges[k]);
  const certGroups = [...(BADGE_GROUPS.Compliance || []), ...(BADGE_GROUPS.Approvals || [])];
  const certs = on.filter(b => certGroups.includes(b));
  const ip = on.find(b => /^IP\d/.test(b)) || '';
  const ik = on.find(b => /^IK\d/.test(b)) || '';
  const criBadge = on.find(b => /^CRI\d+$/.test(b));
  const classBadge = on.find(b => /^Class (I{1,3})$/.test(b)) || '';
  const warrantyBadge = on.map(b => /^(\d+) Year Warranty$/.exec(b)).find(Boolean);

  return {
    'ITEM NAME/NUMBER INTERNAL': s.netsuiteInternal || s.code || '',
    'DISPLAY NAME/CODE-SALES': s.code || '',
    'VENDOR NAME/CODE': '',
    'SALES DESCRIPTION': s.salesDescription || '',
    'PURCHASE DESCRIPTION': s.purchaseDescription || '',
    'INVENTORY CATEGORY': 'Core Range',
    'INVENTORY STATUS': 'Ranged',
    'TYPE': 'LPA Branded',
    'REPRESENTATIVE BRAND': 'LPA',
    'DATASHEET LINK': s.datasheetLink || '',
    'ASSOCIATED PRODUCT/OLD EVO CODE': '',
    'PRODUCT IMAGE': nsImageUrl(productId, s),
    'FAMILY': s.family || s.name || '',
    'SUB CATEGORY': s.variant || '',
    'SERIES': seriesFromCode(s.code),
    'FITTING COLOUR': cs.colour || '',
    'MAIN MATERIAL': cs.material || '',
    'ACCESSORIES/OPTIONS': '',
    'OPTICAL SYSTEM': pd.optic || '',
    'REFLECTOR/BAFFLE COLOUR': '',
    'TILT': digits(pd.tilt),
    'ROTATION': digits(pd.rotation),
    'CERTIFICATIONS': certs.join(', '),
    'SYSTEM WATTAGE (W)': digits(v0.systemPower),
    'SOURCE WATTAGE (W)': digits(v0.sourcePower),
    'SUPPLY VOLTAGE (V)': cs.voltage || '',
    'POWER FACTOR (PF)': pd.powerFactor || '',
    'OPERATING TEMPERATURE (TA)': pd.operatingTemp || '',
    'CLASS OF PROTECTION': pd.class || classBadge,
    'INGRESS PROTECTION RATING (IP)': ip,
    'IMPACT RESISTANCE RATING (IK)': ik,
    'POWER CONNECTION': pd.powerConnection || '',
    'DIMMING OPTIONS': cs.dimming || '',
    'DRIVER BRAND/CODE': pd.driver || '',
    'OUTPUT VOLTAGE (V)': pd.outputVoltage || '',
    'LIGHT SOURCE BRAND': '',
    'LIGHT SOURCE TYPE': '',
    'SYSTEM LUMENS (LM)': digits(pd.lightOutput),
    'SOURCE LUMENS (LM)': digits(v0.sourceLumens),
    'EFFICACY (LM/W)': digits(pd.efficacy),
    'CRI': cs.cri || (criBadge ? '>' + criBadge.replace('CRI', '') : ''),
    'COLOUR TEMP (CCT)': cs.cct || '',
    'MACADAM': digits(pd.macadam),
    'LED LIFETIME (HRS)': digits(cs.ledLifetime),
    'UGR': digits(pd.ugr),
    'LOR %': '',
    'BEAM ANGLE': digits((s.beamCols || [])[0] && s.beamCols[0].angle),
    'ENERGY CLASS': '',
    'ELC ITEM CODE': '',
    'DRIVER MA': '',
    'VENDOR WARRANTY': warrantyBadge ? warrantyBadge[1] + ' Years' : '',
  };
}
